import {
  createAction,
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import { IEditOrganizationModalState } from './interfaces';
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest,
} from 'api/requests';
import {
  IAmenity,
  ICategory,
  IConsultation,
  IFavourite,
  ILocation,
} from 'interfaces/main';
import { fieldName } from 'consts/hardcode';
import { defaultOrganization } from 'consts/default';

// Открытие окна с существующей организацией
export const openEditOrganizationModal = createAction(
  'editOrganizationModal/openEditOrganizationModal',
  (organizationId: number) => ({ payload: organizationId }),
);

// Закрытие окна редактирования организации
export const closeModal = createAction('editOrganizationModal/closeModal');

// Загрузка всей организации по ID
export const getOrganizationById = createAsyncThunk(
  'editOrganizationModal/getOrganizationById',
  async ({ id }: { id: number }, { rejectWithValue }) => {
    try {
      const response = await getRequest(`organization/${id}`, {}, true);

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Загрузка логотипа
export const uploadLogotype = createAsyncThunk(
  'editOrganizationModal/uploadLogotype',
  async (
    { file, organizationId }: { file: File; organizationId: number },
    { rejectWithValue },
  ) => {
    try {
      // 👇 Create new FormData object and append files
      const data = new FormData();
      data.append(`logotype`, file, file.name);

      const response = await postRequest(
        `logotype/upload?organizationId=${organizationId}`,
        data,
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Удаление логотипа
export const removeLogotype = createAsyncThunk(
  'editOrganizationModal/removeLogotype',
  async (id: number, { rejectWithValue }) => {
    try {
      return await deleteRequest(`logotype/${id}`);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Установка значения в поле организации
export const changeOrganization = createAsyncThunk(
  'editOrganizationModal/changeOrganization',
  async (
    {
      key,
      organizationId,
      value,
    }: {
      key: string;
      organizationId: number;
      value: string | number | boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await putRequest(
        `organization/${organizationId}`,
        {
          [key]: value,
        },
        true,
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Добавление нового значения в мультиполе
export const addMultiSelect = createAsyncThunk(
  'editOrganizationModal/addMultiSelect',
  async (
    {
      objectId,
      organizationId,
      key,
    }: {
      objectId: number;
      organizationId: number;
      key: 'complexes' | 'lots' | 'users';
    },
    { rejectWithValue, getState },
  ) => {
    try {
      const field = fieldName[key];
      const { libraries } = getState() as RootState;
      const list = libraries[field.multiple];
      const newItem = list.find(
        (item: ILocation | ICategory | IAmenity | IFavourite | IConsultation) =>
          item.id === objectId,
      );

      const response = await postRequest(
        `organization${field.capitalLetter}`,
        {
          organizationId,
          [field.foreignKey]: objectId,
        },
        true,
      );

      return {
        fieldName: field.multiple,
        data: { ...newItem, [`organization_${field.single}`]: response.data },
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Удаление значения в мультиполе
export const removeMultiSelect = createAsyncThunk(
  'editOrganizationModal/removeMultiSelect',
  async (
    {
      objectId,
      organizationId,
      key,
    }: {
      objectId: number;
      organizationId: number;
      key: 'complexes' | 'lots' | 'users';
    },
    { rejectWithValue },
  ) => {
    try {
      const field = fieldName[key];

      const response = await deleteRequest(
        `organization${field.capitalLetter}`,
        {
          organizationId,
          [field.foreignKey]: objectId,
        },
        true,
      );

      return { fieldName: field.multiple, id: response.data.id };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const initialState: IEditOrganizationModalState = {
  data: defaultOrganization,
  isLoading: false,
  isLogotypeLoading: false,
  isVisible: false,
};

const editOrganizationModalSlice = createSlice({
  name: 'editOrganizationModal',
  initialState,
  reducers: {
    closeModal: () => initialState,
    openEditOrganizationModal: (_, { payload }) => ({
      ...initialState,
      isVisible: true,
      data: {
        ...defaultOrganization,
        id: payload,
      },
    }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOrganizationById.pending, (state) => ({
        ...state,
        isLoading: true,
      }))
      .addCase(getOrganizationById.rejected, (state) => ({
        ...state,
        isLoading: false,
      }))
      .addCase(getOrganizationById.fulfilled, (state, { payload }) => ({
        ...state,
        isLoading: false,
        data: {
          ...state.data,
          ...payload,
        },
      }))
      .addCase(changeOrganization.fulfilled, (state, { payload }) => ({
        ...state,
        data: {
          ...state.data,
          ...payload,
        },
      }))
      .addCase(addMultiSelect.fulfilled, (state, { payload }) => ({
        ...state,
        data: {
          ...state.data,
          [payload.fieldName]: [
            ...state.data?.[payload.fieldName],
            payload.data,
          ],
        },
      }))
      .addCase(removeMultiSelect.fulfilled, (state, { payload }) => ({
        ...state,
        data: {
          ...state.data,
          [payload.fieldName]: state.data?.[payload.fieldName].filter(
            (
              item:
                | ILocation
                | ICategory
                | IAmenity
                | IFavourite
                | IConsultation,
            ) => item.id !== payload.id,
          ),
        },
      }))
      .addCase(uploadLogotype.pending, (state) => ({
        ...state,
        isLogotypeLoading: true,
      }))
      .addCase(uploadLogotype.rejected, (state) => ({
        ...state,
        isLogotypeLoading: false,
      }))
      .addCase(uploadLogotype.fulfilled, (state, { payload }) => ({
        ...state,
        isLogotypeLoading: false,
        data: {
          ...state.data,
          logotype: payload,
        },
      }))
      .addCase(removeLogotype.pending, (state) => ({
        ...state,
        isLogotypeLoading: true,
      }))
      .addCase(removeLogotype.rejected, (state) => ({
        ...state,
        isLogotypeLoading: false,
      }))
      .addCase(removeLogotype.fulfilled, (state, { payload }) => ({
        ...state,
        isLogotypeLoading: false,
        data: {
          ...state.data,
          logotype: null,
        },
      }));
  },
});

// Селекторы
export const EditOrganizationModalSelector = (
  state: RootState,
): IEditOrganizationModalState => state.editOrganizationModal;

export const EditOrganizationModalLoadingSelector = createSelector(
  EditOrganizationModalSelector,
  ({ isLoading }) => isLoading,
);

export const EditOrganizationModalLogotypeLoadingSelector = createSelector(
  EditOrganizationModalSelector,
  ({ isLogotypeLoading }) => isLogotypeLoading,
);

export const EditOrganizationModalVisibleSelector = createSelector(
  EditOrganizationModalSelector,
  ({ isVisible }) => isVisible,
);

export const EditOrganizationModalDataSelector = createSelector(
  EditOrganizationModalSelector,
  ({ data }) => data,
);

export default editOrganizationModalSlice.reducer;
